import { ampli } from 'lib-shared-tracking';
import type { Schedule } from 'lib-shared-types-api';
import moment from 'moment-timezone';
import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';

import { flowsApi } from 'apis/flows';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { getDateAsUTC } from 'utils/date';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { getUserDisplayName, useCurrentUser } from 'utils/user';

import { ScheduleModalProps } from '../../openingHours.view';
import { getUTCOffset } from '../../utils/date';
import { EditModal, EditModalSubmitProps } from '../editModal/editModal';

// When duplicating schedules, every opening hours entries should
// be updated with following:
//  - the current date
//  - the current agent name
//  - a new id
//  - a new start date (if start_date is in the past)
//  - a new overridden_by id what has to be indentical,
//    with the new id of overridding item
function adjustOpeningHours(
  openingHours: Schedule['opening_hours'],
  agentName: string,
): Schedule['opening_hours'] {
  const newIds = Object.fromEntries(openingHours.map(({ id }) => [id, uuid()]));

  return openingHours
    .map((openingHour) => {
      if (openingHour.opening_hour_type === 'regular') {
        const dayOfWeek = openingHour.recurring?.day_of_week as number;
        const today = moment().isoWeekday();
        const momentWeekday = moment()
          .isoWeekday(dayOfWeek)
          .startOf('day')
          .add(getUTCOffset(), 'minutes');

        return {
          ...openingHour,
          created_at: moment().toISOString(),
          created_by: agentName,
          id: newIds[openingHour.id],
          start_date:
            today <= dayOfWeek
              ? getDateAsUTC(momentWeekday).toISOString()
              : getDateAsUTC(momentWeekday.add(1, 'weeks')).toISOString(),
        };
      }

      return {
        ...openingHour,
        created_at: moment().toISOString(),
        created_by: agentName,
        id: newIds[openingHour.id],
        start_date: openingHour.next_date as string,
      };
    })
    .map((openingHour) => ({
      ...openingHour,
      ...(!!openingHour.overridden_by && {
        overridden_by: {
          ...openingHour.overridden_by,
          id: newIds[openingHour.overridden_by.id],
        },
      }),
    }));
}

export const DuplicateModal = ({
  schedule: { id, name: scheduleName },
  onClose,
}: Omit<ScheduleModalProps, 'refetchItems'>): JSX.Element => {
  const { data: readFlowScheduleResponse } = flowsApi.useReadFlowScheduleQuery({
    id,
  });
  const [createSchedule, { isLoading }] =
    flowsApi.useCreateFlowScheduleMutation();
  const navigate = useAppNavigate();
  const notify = useNotifications();
  const { user } = useCurrentUser();
  const agentName = getUserDisplayName(user);

  const handleSubmit = useCallback(
    async ({ name }: EditModalSubmitProps) => {
      ampli.businessHoursDuplicateScheduleFormSubmitted();

      if (readFlowScheduleResponse) {
        const res = await createSchedule({
          ...readFlowScheduleResponse,
          created_at: moment.utc().toISOString(),
          name,
          opening_hours: adjustOpeningHours(
            readFlowScheduleResponse.opening_hours,
            agentName,
          ),
        });

        if ('data' in res) {
          notify(
            {
              title: 'Schedule duplicated!',
              time: 5000,
            },
            NotificationType.Success,
          );

          navigate({
            path: '/settings/businesshours/:id',
            params: {
              path: {
                id: res.data.id,
              },
            },
          });
        } else {
          notify(
            {
              title: 'An error occurred when trying to duplicate schedule.',
            },
            NotificationType.Error,
          );
        }
      } else {
        notify(
          {
            title: 'An error occurred when trying to fetch current schedule.',
          },
          NotificationType.Error,
        );
      }
    },
    [agentName, navigate, readFlowScheduleResponse, createSchedule, notify],
  );

  return (
    <EditModal
      confirmName="Duplicate"
      isLoading={isLoading}
      name={`${scheduleName} (copy)`}
      onClose={() => {
        onClose();
        ampli.businessHoursDuplicateScheduleFormCancelled();
      }}
      onSubmit={handleSubmit}
      title="Duplicate schedule"
    />
  );
};
